/**
 * The docs page in Dutch (Flemish, with "je" and "jullie"), the default. It
 * keeps the wording of De Ceuster's Dutch connector guide where it fits.
 * Claude's menu names and buttons stay as Claude shows them, in English.
 */

import type { DocsContent } from "./types";

export const nl: DocsContent = {
  page: {
    title: "Handleiding",
    description: "Zo gebruik je de connector DC Data in Claude.",
    contentsLabel: "Inhoud",
    pagerLabel: "Vorige en volgende pagina",
    previous: "Vorige",
    next: "Volgende",
  },

  pages: {
    "connect-claude": "Claude verbinden",
    "what-you-can-ask": "Wat je kan vragen",
    tools: "De tools",
    limits: "Wat niet kan",
    "privacy-and-data": "Privacy en gegevens",
    troubleshooting: "Vragen en problemen",
  },

  connect: {
    intro:
      "Twee stappen: iemand met de rol Owner in jullie Claude-organisatie voegt de connector één keer toe, voor iedereen. Daarna verbind je hem zelf, met je eigen account.",
    introEveryoneFirst:
      "Je verbindt de connector zelf, met je eigen account. Staat **DC Data** nog niet in je lijst, dan voegt iemand met de rol Owner in jullie Claude-organisatie hem eerst één keer toe, voor iedereen. Die stappen staan onderaan.",
    modeOff:
      "De connector staat nog niet aan. Zodra hij aanstaat, werken deze stappen en verschijnt hier de URL om toe te voegen.",
    modeLocal:
      "Deze connector draait in lokale testmodus, zonder aanmelden, alleen op deze computer. Voeg de URL toe aan een lokale MCP-client, zoals Claude Code of de MCP Inspector.",
    ownerHeading: "Eenmalig, door een Owner van de Claude-organisatie",
    ownerSteps: [
      "Open in Claude **Organization settings → Connectors**, klik op **Add** en kies dan **Custom → Web**.",
      "Geef hem de naam **DC Data** en plak deze URL:",
      "Klik daarna in de lijst op **DC Data** en zet bij **Tool permissions** de groep **Read-only tools** op **Always allow**. Alle tools lezen enkel, dus opzoekingen mogen dan lopen zonder goedkeuring.",
    ],
    ownerFolded: "Voor de Owner van de Claude-organisatie",
    everyoneHeading: "Daarna, voor iedereen",
    everyoneFirstHeading: "Voor iedereen, met je eigen account",
    everyoneSteps: [
      "Open Claude, in de browser of de app, en ga naar **Customize → Connectors**.",
      "Zoek **DC Data** en klik op **Connect**.",
      "Meld je aan met je De Ceuster Microsoft-account, je gewone werkaccount.",
      "Klik daarna op **DC Data** en zet bij **Tool permissions** de groep **Read-only tools** op **Always allow**, zodat Claude niet bij elke vraag om toestemming vraagt.",
      "Test het in een nieuw gesprek: “Met welk account ben ik aangemeld bij DC Data?” Claude antwoordt met je naam en e-mailadres.",
    ],
    accessNote:
      "Iedereen met een De Ceuster Microsoft-account kan zich aanmelden. Met een gastaccount lukt dat niet.",
    urlLoading: "De URL wordt geladen…",
    urlNotOnYet: "De URL verschijnt hier zodra de connector aanstaat.",
    urlFailed: "De URL kon niet geladen worden. Herlaad de pagina om het opnieuw te proberen.",
  },

  copyUrl: {
    button: "Kopiëren",
    copied: "Gekopieerd",
    label: "De URL van de connector kopiëren",
    copiedStatus: "De URL van de connector is gekopieerd.",
    failed: "Kopiëren lukte niet: selecteer de URL en kopieer hem zelf.",
  },

  capabilities: {
    intro:
      "Met de connector leest Claude jullie Odoo: klanten, werven, kranen, orders, huurcontracten, facturen, betalingen en omzet. De connector kan alleen lezen. Stel je vraag gewoon, in het Nederlands of het Engels, zoals je ze aan een collega zou stellen. Claude kiest zelf de tools en leest Odoo live: je krijgt de stand van dat moment.",
    toolsNote:
      "Dit zijn de tools die jij in Claude krijgt. Met de meeste leest Claude jullie Odoo. Je hoeft hun namen niet te kennen, want Claude kiest zelf. Ze helpen wel om te zien wat Claude heeft opgezocht. Op de pagina Tools zet je een tool voor jezelf uit, zodat Claude hem in jouw gesprekken niet meer gebruikt.",
    toolsLink: "Je tools en of ze aanstaan",
    toolsLoading: "De tools worden geladen…",
    toolsFailed:
      "De tools konden niet geladen worden. Herlaad de pagina om het opnieuw te proberen.",
    toolSeparator: ": ",
    examplesHeading: "Vraag bijvoorbeeld",
    quotes: ["“", "”"],
    examples: {
      revenue: "Wat was onze omzet per maand in 2025?",
      overdue: "Welke facturen van [klant] zijn vervallen, en hoe lang al?",
      "on-rent": "Welke van onze kranen staan nu verhuurd, en bij welke klanten?",
      rentals: "Welke huurcontracten lopen er nu bij [klant]?",
      deliveries: "Welke orders worden volgende week geleverd, en op welke werf?",
      map: "Zet onze werven van het derde kwartaal op een kaart, met ringen rond Geel.",
    },
    examplesNote: "Waar [klant] staat, vul je de naam van een klant in.",
    tipsHeading: "Tips",
    tips: {
      period: {
        title: "Noem een periode",
        body: "“In 2025”, “van 1 tot 31 maart”, “vorige maand”. Zonder periode zoekt Claude breed en wordt het antwoord groot.",
      },
      "client-first": {
        title: "Eerst de klant, dan de details",
        body: "Laat Claude eerst de klant zoeken en kies de juiste als er meerdere zijn. Vraag daarna naar de orders, huurcontracten of facturen van die klant. Zo werkt Claude met de juiste klant uit Odoo en niet met een naamgenoot.",
      },
      "say-what-you-mean": {
        title: "Zeg wat je bedoelt",
        body: "Claude toont bevestigde orders en huurcontracten; wil je ook offertes zien, vraag er dan uitdrukkelijk naar. Orders zoekt Claude op orderdatum: bedoel je de leverdatum, zeg het dan.",
      },
      form: {
        title: "Vraag de vorm die je nodig hebt",
        body: "Een tabel, een grafiek, een kaart, of een artifact: een interactieve pagina naast het gesprek.",
      },
    },
  },

  limits: {
    "changes-nothing": {
      title: "Niets wijzigen",
      body: "Claude kan via de connector geen orders, offertes, facturen of klanten aanmaken of aanpassen in Odoo, en verstuurt niets.",
    },
    "not-everything": {
      title: "Niet alles in Odoo is zichtbaar",
      body: "Claude ziet de betalingen die op de bank binnenkwamen en de facturen die ze voldeden. Een betaling die nog niet aan een factuur gekoppeld is, telt Claude apart. Zo'n factuur staat in Odoo nog open, hoewel ze al betaald kan zijn. De eigendomsgeschiedenis van kranen, en leasing en verzekering per kraan toont de connector niet.",
    },
    "map-approximate": {
      title: "Plaatsen op de kaart zijn bij benadering",
      body: "Odoo bewaart geen coördinaten van werven. Claude zet een werf daarom op het midden van haar postcode, of van haar gemeente als de postcode ontbreekt, en meet de afstand tot Geel in vogelvlucht. Werven in het buitenland, of zonder bruikbare postcode of gemeente, komen niet op de kaart.",
    },
    "closed-rental-site": {
      title: "De werf van een afgesloten huurcontract",
      body: "Odoo zet de werf van een afgesloten huurcontract soms terug op jullie depot. Claude neemt dan de werf van de laatste factuur.",
    },
    "no-end-date": {
      title: "Geen werkelijke einddatum van een huur",
      body: "Odoo kent de start, de geschatte einddatum en de dag waarop het contract in Odoo werd afgesloten, niet de dag waarop de kraan echt terugkwam. “Welke kranen stonden vorige maand verhuurd” is dus een goede benadering, geen exacte telling.",
    },
    "occupancy-per-type": {
      title: "Bezettingsgraad en beschikbaarheid per kraantype",
      body: "Claude geeft de bezettingsgraad per dag, week of maand vanaf 1 januari 2024, voor de hele vloot, voor de snelmontagekranen en de torenkranen, en per kraantype. Vanaf vandaag telt Claude de geboekte kranen, tot hoogstens een jaar vooruit. Claude zegt ook welke kranen in een periode vrij zijn en welke meldingen de app Bezettingsgraad geeft. Claude berekent dat allemaal uit Odoo op het moment dat je het vraagt, met de regels van de app Bezettingsgraad, en vermeldt wanneer Odoo gelezen werd. Claude gebruikt dezelfde lezing van Odoo hoogstens 10 minuten, dus een boeking van daarnet kan pas na enkele minuten meetellen. Toont Odoo de kranen niet, dan zegt Claude dat en geeft het geen schatting. De hoogte en de giek van een kraan staan niet in Odoo, dus de beschikbaarheid gaat per kraantype en niet per maat.",
    },
    "finance-checks": {
      title: "Niet-gefactureerd werk en transportprijzen",
      body: "Claude kan werk tonen dat geleverd is maar niet of maar deels gefactureerd, en transporten die ver onder het tarief gefactureerd werden. Claude berekent beide uit Odoo op het moment dat je het vraagt, en vermeldt wanneer Odoo gelezen werd. Claude gebruikt dezelfde lezing van Odoo hoogstens 10 minuten, dus een factuur van daarnet kan pas na enkele minuten meetellen. Werk dat op een andere manier gefactureerd lijkt, bijvoorbeeld via een creditnota, een andere factuur of de verkoop van een kraan, laat Claude weg. Werk aan jullie eigen huurkranen telt niet mee in wat klanten nog verschuldigd zijn en staat apart. Odoo kent de kosten van een transport niet, dus een prijs onder het tarief is geen verlies. Klantnamen ziet enkel wie Klanten heeft.",
    },
    "outside-odoo": {
      title: "Niets buiten Odoo",
      body: "De planning in Visual Planner, de Ford-app, jullie mails, en de reden waarom een offerte verloren ging.",
    },
    "left-out": {
      title: "Bewust weggelaten",
      body: "Contactgegevens (e-mail, telefoon), bankgegevens, personeelsgegevens en interne notities uit Odoo.",
    },
    "answer-size": {
      title: "Een maximale grootte per antwoord",
      body: "Per opzoeking haalt Claude een beperkte hoeveelheid op. Is het antwoord te groot, dan wordt het ingekort en zegt Claude dat.",
    },
  },

  privacy: {
    "own-account": {
      title: "Je eigen account",
      body: "Iedereen meldt zich aan met zijn eigen De Ceuster Microsoft-account. Gaat iemand weg, dan schakelt de IT-beheerder van De Ceuster het account uit, en binnen ongeveer een uur vervalt de toegang. Heeft iemand de connector niet meer nodig, of moet de toegang sneller stoppen, dan blokkeert een beheerder die persoon meteen bij Mensen en teams, op het tabblad Teams. Velta kan dat ook voor jullie doen.",
    },
    "same-for-everyone": {
      title: "Dezelfde tools, dezelfde gegevens",
      body: "De connector leest Odoo via één account dat alleen kan lezen. Wat iemand ziet, hangt dus niet af van zijn eigen rechten in Odoo. Met dezelfde tools ziet iedereen dezelfde Odoo-gegevens, zoals de Companyweb-score of de minimumprijs en marge van een kraan. Openstaande facturen per klant ziet enkel wie facturen mag zoeken, via Financieel in zijn team of een persoonlijke uitzondering. Zo tonen ook de tools van de bezettingsgraad en die van niet-gefactureerd werk en transportprijzen klantnamen enkel aan wie Klanten heeft.",
    },
    "passes-through": {
      title: "Gegevens worden enkel doorgegeven",
      body: "De connector geeft Odoo-gegevens door aan Claude en bewaart ze niet. Enkel je aanmelding wordt bewaard, versleuteld, zodat je je niet telkens opnieuw moet aanmelden. Het antwoord staat daarna in je gesprek in Claude, zoals alles wat je in Claude doet.",
    },
    "log-metadata": {
      title: "Het logboek bewaart enkel metadata",
      body: "Wie welke tool gebruikte, wanneer, hoe lang het duurde en of het lukte, om de dienst draaiend te houden. Het logboek bewaart dat 12 maanden en verwijdert daarna alles automatisch. Wat er gevraagd of geantwoord werd, wordt nooit bewaard of gelogd.",
    },
  },

  roles: {
    heading: "Wie wat ziet op het dashboard",
    intro:
      "Iedereen heeft één rol op dit dashboard. Die rol staat los van je rol in jullie Claude-organisatie. Bij je eerste aanmelding ben je Lid. Een beheerder kan je rol daarna wijzigen bij Mensen en teams.",
    access: "Toegang",
    yes: "Ja",
    no: "Nee",
    rows: {
      connector: "De connector DC Data in Claude, met de tools van je team",
      "start-and-docs": "Start en Handleiding",
      apps: "De apps die je team heeft, zoals Bezettingsgraad",
      "own-switches": "Een tool voor jezelf uitzetten, op de pagina Tools",
      "admin-pages":
        "Rollen, teams en iemand blokkeren bij Mensen en teams, wie welke tool krijgt op Tools, Statistieken en Logboek",
      "issue-notes":
        "De geschiedenis van een probleem bij Fouten in Statistieken lezen en er een notitie aan toevoegen",
      roadmap: "De roadmap volgen en er ideeën voor voorstellen, met de voortgang van elke maand",
      "roadmap-budget":
        "Het budget van elke maand en de uren en dagen van het werk op de roadmap zien, en het budget wijzigen voor deze maand en later",
      "roadmap-work":
        "Ideeën rechtstreeks op de roadmap zetten, de ideeën en vragen in Triage beoordelen, de inspanning van items bepalen, items verplaatsen en de Linear-tickets zien",
      "issue-work":
        "Een probleem bij Fouten in Statistieken in behandeling nemen, oplossen, negeren of heropenen, en met de problemen werken vanuit Claude",
      "pause-tools": "Een tool tijdelijk uitzetten voor iedereen, op de pagina Tools",
      "view-as":
        "Het dashboard bekijken als een collega of een rol, zonder iets te kunnen wijzigen, bij Bekijken als in Instellingen",
    },
    note: "Beheerders, eigenaars en ontwikkelaars zitten altijd in het team Beheerders. Dat team heeft alle tools en alle apps.",
  },

  faq: {
    "not-listed": {
      question: "DC Data staat niet bij mijn connectors",
      answer: [
        "Een Owner van jullie Claude-organisatie voegt de connector één keer toe, voor iedereen (zie [[connect-claude]]). Vraag die persoon om hem toe te voegen.",
      ],
    },
    "sign-in-fails": {
      question: "Aanmelden lukt niet",
      answer: [
        "Meld je aan met je eigen De Ceuster Microsoft-account, je gewone werkaccount. Gastaccounts en accounts van andere organisaties worden geweigerd. Lukt het nog altijd niet, vraag dan aan een beheerder of aan Velta of je account geblokkeerd is, of aan de IT-beheerder van De Ceuster of het nog actief is.",
      ],
    },
    "not-from-odoo": {
      question: "Claude antwoordt, maar niet uit Odoo",
      answer: [
        "Een antwoord uit Odoo toont een stap van DC Data, die je kan openklappen om te zien wat Claude opzocht. Staat die stap er niet, dan komt het antwoord niet uit Odoo. Kijk of de connector aanstaat voor het gesprek, in het menu met tools onder het invoerveld.",
      ],
    },
    "cut-short": {
      question: "Het antwoord werd ingekort",
      answer: [
        "Maak de vraag kleiner: een kortere periode, één klant, één kraan of één soort order. Of vraag een samenvatting (omzet per maand) in plaats van elke factuur apart.",
      ],
    },
    slow: {
      question: "Het duurt lang, of Odoo is te druk",
      answer: [
        "Odoo antwoordt in een beperkt tempo, dus een grote vraag kan een minuut of langer duren, en nog langer als meerdere collega’s tegelijk vragen stellen. Meldt Claude dat Odoo te traag of te druk was, probeer dan na een minuut opnieuw.",
      ],
    },
    "number-wrong": {
      question: "Een cijfer lijkt niet te kloppen",
      answer: [
        "Controleer belangrijke cijfers in Odoo voor je ze doorgeeft of erop beslist.",
        [
          "Omzet en orderbedragen zijn exclusief btw. Het totaal van een factuur en wat nog openstaat of vervallen is, zijn inclusief btw. Claude zegt erbij welke van de twee het geeft.",
          "Omzet telt zonder wat jullie aan de eigen De Ceuster-bedrijven factureren; ook de kaart en de klantenlijst laten die bedrijven weg.",
          "De gegevens in Odoo beginnen halverwege 2023: vergelijk jaren vanaf 2024.",
        ],
      ],
    },
    "how-looked-up": {
      question: "Hoe zocht Claude het op?",
      answer: [
        "Vraag het: “Welke periode en filters heb je gebruikt?” Zo zie je meteen of Claude je vraag goed begreep.",
      ],
    },
  },
};
